// src/components/reports/CompanyTrendReport/SummaryCards.tsx

import React from 'react';
import { Grid, Card, CardContent, Typography } from '@mui/material';
import { CompanyTrendReportItem, formatCurrency, formatProfitRate, formatYoYRate, formatPeriodLabel } from '../../../../types/sales';

interface SummaryCardsProps {
  reportData: CompanyTrendReportItem[];
  startMonth: string;
  endMonth: string;
}

export const SummaryCards: React.FC<SummaryCardsProps> = ({
  reportData,
  startMonth,
  endMonth
}) => {
  // 最新期間のデータを取得
  const getLatestPeriodData = (): CompanyTrendReportItem | null => {
    if (!reportData || reportData.length === 0) return null;
    return reportData[reportData.length - 1];
  };

  // 合計値を計算
  const calculateTotals = (): {
    total_sales: number;
    final_profit: number;
    profit_rate: number;
    project_count: number;
  } => {
    const totals = {
      total_sales: 0,
      final_profit: 0,
      profit_rate: 0,
      project_count: 0
    };
    
    if (reportData) {
      reportData.forEach((item: CompanyTrendReportItem) => {
        totals.total_sales += Number(item.total_sales) || 0;
        totals.final_profit += Number(item.final_profit) || 0;
        totals.project_count += Number(item.project_count) || 0;
      });
      
      if (totals.total_sales > 0) {
        totals.profit_rate = (totals.final_profit / totals.total_sales) * 100;
      }
    }
    
    return totals;
  };

  const latestData = getLatestPeriodData();
  const totals = calculateTotals();

  if (!latestData) return null;

  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              売上合計
            </Typography>
            <Typography variant="h4" component="div">
              {formatCurrency(totals.total_sales)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {`${startMonth} 〜 ${endMonth}`}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              最終利益
            </Typography>
            <Typography
              variant="h4"
              component="div"
              color={totals.final_profit < 0 ? 'error.main' : 'success.main'}
            >
              {formatCurrency(totals.final_profit)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              利益率: {formatProfitRate(totals.profit_rate)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              最新期間
            </Typography>
            <Typography variant="h5" component="div">
              {formatPeriodLabel(latestData.period)}
            </Typography>
            <Typography
              variant="body1"
              color={latestData.sales_yoy && latestData.sales_yoy > 0 ? 'success.main' : 'error.main'}
            >
              前年同期比: {formatYoYRate(latestData.sales_yoy)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              完了工事件数
            </Typography>
            <Typography variant="h4" component="div">
              {totals.project_count}件
            </Typography>
            <Typography variant="body2" color="textSecondary">
              対象期間合計
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};